import React, { FC, memo, ReactElement } from 'react'
import { AdProps, LayoutProps } from '@sport1/types/web'
import Script from 'next/script'
import Config from '@/utils/Config'
import min from '@/utils/text/min'
import { createAdsPlacements } from '@/utils/ads/display/createAdsPlacements'
import { AdDataProps } from '@/components/Ads/types/AdsTypes'
import { extractMatchTeams } from '@/utils/ads/utils'

const getAdMeta = (layout: LayoutProps | undefined) => {
    const adKeys = layout?.meta?.ad?.adKeys

    const targeting =
        layout?.meta?.ad?.targeting ||
        (layout?.components?.find(component => (component as AdProps)?.ad?.targeting) as AdProps)
            ?.ad.targeting ||
        {}
    const keywords = adKeys

    const zone = Object.entries(targeting)
        .filter(([key]) => key.startsWith('adUnit') && key !== 'adUnit1')
        .sort()
        .map(entry => entry[1])
        .join('/')

    const playingTeams = extractMatchTeams(layout)
    const team = 'team' in targeting ? targeting.team : playingTeams
    const section = targeting.section
    const adUnit3 = targeting.adUnit3
    const adUnit4 = targeting.adUnit4
    const targetingTeam = targeting.team
    const contentType = targeting.contentType

    return { keywords, section, team, zone, adUnit3, adUnit4, targetingTeam, contentType }
}

const AdSSetup: FC<AdDataProps> = ({ path = '', meta, ads, layoutData }): ReactElement | null => {
    if (false === Config.ADS_ACTIVE || false === layoutData?.meta?.ad?.adsEnabledForLayout) {
        return null
    }

    const mobileAdProps = createAdsPlacements({
        isMobile: true,
        path,
        meta,
        ads,
        layoutData,
    })
    const desktopAdProps = createAdsPlacements({
        isMobile: false,
        path,
        meta,
        ads,
        layoutData,
    })

    const {
        keywords = [],
        team = '',
        section = '',
        zone,
        adUnit3,
        adUnit4,
        contentType,
        targetingTeam,
    } = getAdMeta(layoutData)

    return (
        <>
            {/**
             * We need to know the device type really fast to support
             * CLS protection. See css/ads.css
             */}
            <Script
                id="ads-device-detection-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                    const isMobile = window.innerWidth <= ${Config.MOBILE_THRESHOLD};
                    document.documentElement.classList.add(isMobile? 's1-device-mobile' : 's1-device-desktop');
                    if(isMobile){
                        const isAppleMobileDevice = (/iPhone|iPad/i.test(window.navigator.userAgent) || /iPhone|iPad/i.test(window.navigator.platform))
                        document.documentElement.classList.add(isAppleMobileDevice ? 's1-device-mobile-ios' : 's1-device-mobile-android');
                    }`,
                }}
            />

            {/**
             * To make the placementCounter in the AdPlacementProvider
             * work, we'll make the initial ad placements globally
             * avaliable here. See the AdPlacementProvider for more
             * info (look for `workaround`).
             */}
            <Script
                id="ads-placement-init-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
                    window.adPlacements = window.innerWidth <= ${
                        Config.MOBILE_THRESHOLD
                    } ? ${JSON.stringify(mobileAdProps.adPlacements)} : ${JSON.stringify(
                        desktopAdProps.adPlacements
                    )};`,
                }}
            />

            {/* a stub for Ströer */}
            <Script
                id="ads-stroeer-stub-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: min(`
                        window.SDG = window.SDG || {};
                        window.SDG.cmd = window.SDG.cmd || [];
                        window.SDG.cmd.push(function() {
                            SDG.Publisher.setZone("${zone}");
                            SDG.Publisher.addKeywords(${JSON.stringify(keywords)});
                            const pkv1 =${JSON.stringify(adUnit3)};
                            const pkv2 =${JSON.stringify(adUnit4)};
                            const pkv3 =${JSON.stringify(targetingTeam)};
                            const pkv4 =${JSON.stringify(contentType)};
                            if(pkv1) SDG.Publisher.addKeyValue("pkv1", pkv1);
                            if(pkv2) SDG.Publisher.addKeyValue("pkv2", pkv2);
                            if(pkv3) SDG.Publisher.addKeyValue("pkv3", pkv3);
                            if(pkv4) SDG.Publisher.addKeyValue("pkv4", pkv4);
                            if ('${section}') SDG.Publisher.addKeyValue("section", "${section}");
                            var teamData = ${JSON.stringify(team)};
                            if (teamData) {
                                if (Array.isArray(teamData)) {
                                    SDG.Publisher.addKeyValues({ "team": teamData });
                                    console.debug('Ads: SDG.Publisher.addKeyValues({"team": ' + JSON.stringify(teamData) + '})')
                                } else {
                                    SDG.Publisher.addKeyValue("team", teamData);
                                    console.debug('Ads: SDG.Publisher.addKeyValue("team", "' + teamData + '")');
                                }
                            }
                            const KEY_NAME = 'CxSegments';
                            SDG.Advertising.getLocalStorageData(KEY_NAME).then(function(values){
                                const cxSegments = values.split(',');
                                SDG.Publisher.addKeyValues({[KEY_NAME]: cxSegments});
                                console.debug('Ads: SDG.Publisher.addKeyValues({"'+KEY_NAME+'": '+JSON.stringify(cxSegments)+"})");
                            }).catch(function(){
                                SDG.Publisher.addKeyValues({[KEY_NAME]: 'n_a'});
                                console.debug('Ads: SDG.Publisher.addKeyValues({"'+KEY_NAME+'": "n_a")');
                            });

                            const ADS_PI_KEY_NAME = 'ADS_PI';
                            let ADS_PI_VALUE = 1;
                            SDG.Advertising.getSessionStorageData(ADS_PI_KEY_NAME).then(function(value){
                                ADS_PI_VALUE = value? parseInt(value) : 1;
                                SDG.Publisher.addKeyValue("pi",String(ADS_PI_VALUE));
                                console.debug('Ads: SDG.Publisher.addKeyValue("pi", "' + ADS_PI_VALUE + '")');
                            }).catch(function(){
                                SDG.Publisher.addKeyValue("pi",String(ADS_PI_VALUE));
                                console.debug('Ads: SDG.Publisher.addKeyValue("pi", "' + ADS_PI_VALUE + '")');
                            }).finally(function(){
                                ADS_PI_VALUE++;
                                SDG.Advertising.setSessionStorageData(ADS_PI_KEY_NAME, ADS_PI_VALUE).then(function(){
                                    console.debug('Ads: SDG.Advertising.setSessionStorageData("pi", "' + ADS_PI_VALUE + '")');
                                }).catch(function(){
                                    console.debug('Ads: SDG.Advertising.setSessionStorageData - error');
                                });
                            });

                            console.debug('Ads: SDG.Publisher.setZone("${zone}")');
                            console.debug('Ads: SDG.Publisher.addKeywords(${JSON.stringify(
                                keywords
                            )})');
                            console.debug('Ads: SDG.Publisher.addKeyValue("section", "${section}")');
                        });
                    `),
                }}
            />
        </>
    )
}

export default memo(AdSSetup)
